import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const loginInProgress = React.useRef(false);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    } else {
      if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
        delete axios.defaults.headers.common['Authorization'];
      }
      if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
        delete api.defaults.headers.common['Authorization'];
      }
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          // Verify token is still valid
          if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          }
          if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          }
          const response = await api.get('/auth/verify');
          
          if (response.status === 200 && response.data?.user) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          } else {
            // Token is invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
              delete axios.defaults.headers.common['Authorization'];
            }
            if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
              delete api.defaults.headers.common['Authorization'];
            }
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
            delete axios.defaults.headers.common['Authorization'];
          }
          if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
            delete api.defaults.headers.common['Authorization'];
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Track the last token that triggered a login-success notification so the
  // toast isn't re-fired on re-renders or StrictMode double-invocations.
  const lastNotifiedToken = React.useRef(null);

  const login = async (email, password) => {
    // Prevent multiple simultaneous login attempts
    if (loginInProgress.current) {
      return { success: false, message: 'Login already in progress' };
    }

    try {
      loginInProgress.current = true;
      setLoading(true);
      const response = await api.post('/auth/login', {
        email,
        password
      });

      // Handle successful login response
      if (response.data.token && response.data.user) {
        const { token: newToken, user: userData } = response.data;

        // Store in localStorage
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));

        // Update state
        setToken(newToken);
        setUser(userData);

        // Set axios default header
        if (api && api.defaults && api.defaults.headers && api.defaults.headers.common) {
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        }

        // Dismiss any lingering login-success toast from a prior attempt
        // and only fire the success toast once per token. This prevents the
        // notification from re-appearing on StrictMode re-renders or when
        // the Login component re-mounts.
        toast.dismiss('login-success');
        if (lastNotifiedToken.current !== newToken) {
          lastNotifiedToken.current = newToken;
          toast.success('Login successful!', { id: 'login-success' });
        }
        return { success: true };
      } else {
        // Handle case where backend returns unexpected response structure
        const message = response.data?.error || 'Invalid credentials';
        toast.error(message);
        return { success: false, message };
      }
    } catch (error) {
      // Handle HTTP errors (400, 401, etc.)
      const message = error.response?.data?.error || 'Login failed. Please try again.';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
      loginInProgress.current = false;
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/register', userData);

      if (response.status === 201 || response.data?.user) {
        toast.success('Registration successful! Please login.');
        return { success: true };
      } else {
        const message = response.data?.error || 'Registration failed';
        toast.error(message);
        return { success: false, message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear state
    setToken(null);
    setUser(null);

    // Remove axios default header
    delete axios.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['Authorization'];

    // Dismiss any login toast and reset the once-per-token guard so the
    // next login fires a fresh success notification.
    toast.dismiss('login-success');
    lastNotifiedToken.current = null;

    toast.success('Logged out successfully');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasPermission = (permission) => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;